/**
 * Scroll the page to the very top. Targets every plausible scroll container
 * because risk forms can be rendered either in the public route (window
 * scrolls) or inside the app shell where `<main>` is the actual scrolling
 * element with `overflow-y: auto`.
 */
export const scrollFormToTop = (behavior: ScrollBehavior = "smooth") => {
  try {
    window.scrollTo({ top: 0, behavior });
  } catch {
    // ignore — older browsers without smooth option
    try {
      window.scrollTo(0, 0);
    } catch {}
  }
  if (typeof document === "undefined") return;
  // Scroll any document-level scroll containers (html/body in some browsers).
  try {
    document.documentElement?.scrollTo?.({ top: 0, behavior });
    document.body?.scrollTo?.({ top: 0, behavior });
  } catch {}
  // Scroll every <main> on the page — the app shell uses one with overflow:auto.
  document.querySelectorAll("main").forEach((m) => {
    try {
      m.scrollTo({ top: 0, behavior });
    } catch {}
  });
};

/**
 * Zusätzlich zu `scrollFormToTop`: alle **scrollenden Vorfahren** eines
 * Elements nach oben holen.
 *
 * Gebraucht wird das im schwebenden Fenster. Dort scrollt weder `window` noch
 * ein `<main>`, sondern ein `<div>` mit `overflow-y: auto` innerhalb der
 * Fenster-Schale — `scrollFormToTop` erwischt genau diesen Container nicht, und
 * ein Schrittwechsel blieb deshalb auf halber Höhe stehen.
 */
export const scrollScrollableAncestorsToTop = (
  el: HTMLElement | null,
  behavior: ScrollBehavior = "smooth",
) => {
  if (!el || typeof window === "undefined") return;
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    // `getComputedStyle` fehlt in keinem Browser, in jsdom aber liefert es
    // "visible" — dort passiert schlicht nichts, und werfen darf es nie.
    try {
      const overflowY = window.getComputedStyle(cur).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && cur.scrollTop > 0) {
        if (typeof cur.scrollTo === "function") {
          cur.scrollTo({ top: 0, behavior });
        } else {
          cur.scrollTop = 0;
        }
      }
    } catch {}
    cur = cur.parentElement;
  }
};
